/**
 * Value type for a single message in a chat export.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/ChatExportRecord.swift` in spirit
 * but is intentionally narrower for the web slice: the web export only
 * needs the fields the UI renders in the per-thread export view, not
 * the full forensic shape iOS carries (reactions, attachment-kind enums,
 * tombstone flags). Those fields will be added in a follow-up if the
 * web export grows beyond "read me back this transcript."
 *
 * THREAT MODEL
 *   The single load-bearing invariant of this shape is that
 *   `fromDisplayName` is sourced ONLY from the LOCAL roster snapshot
 *   passed in at format time — never from a `from`/`displayName` field
 *   on a peer's envelope. The peer can lie about their displayed name
 *   on every message they send; an attacker who flipped names on the
 *   server cannot retroactively rewrite the user's exported chat
 *   history because the formatter never reads such a field.
 *
 *   To make that hard to regress: the type below carries
 *   `fromDisplayName` but the formatter resolves it from the roster,
 *   and the self-test verifies that an absent roster entry results in
 *   "Unknown user" (i.e. the record-level field is NOT trusted as a
 *   fallback by the formatter).
 *
 *   The accompanying `LocalRoster` type is the only path by which a
 *   display name reaches the file on disk.
 */

/**
 * A single exported message. `fromDisplayName` is carried for caller
 * convenience (so an upstream collector can attach the name it already
 * resolved) but the formatter ignores it and re-resolves from the
 * `LocalRoster` at format time. See file header.
 */
export interface ChatExportRecord {
  /** Local message identifier. Same id the rest of the E2EE layer uses. */
  readonly messageId: string;
  /**
   * Authenticated sender userId. For inbound messages, this is the
   * userId carried in the envelope's signed header — never a
   * server-side `from` field, never a display name.
   */
  readonly fromUserId: string;
  /**
   * The display name as the *caller* resolved it. The formatter does
   * NOT trust this field — it re-resolves from the `LocalRoster`
   * passed in. Carried on the record so call sites that already have
   * a name don't have to thread the roster everywhere; carried with
   * the understanding that the formatter is the one source of truth.
   */
  readonly fromDisplayName: string;
  /** Unix milliseconds the message was originally sent. */
  readonly sentAt: number;
  /** Plaintext body of the message. */
  readonly body: string;
  /**
   * Optional human-readable summary of an attachment, e.g.
   * `"image (1.2 MB)"`. The export deliberately never embeds the
   * encrypted bytes or the CDN URL — re-acquiring an attachment is a
   * separate flow.
   */
  readonly mediaSummary?: string;
}

/**
 * Snapshot of the local user roster at format time. Keyed by userId →
 * locally-trusted display name. The ONLY way a display name reaches
 * the file on disk. A missing entry causes the formatter to render
 * `"Unknown user"` for that sender.
 *
 * Roster source: the caller's local username book — populated from
 * profile-key-encrypted self-attestations the user has accepted, never
 * from server-side `from`-field strings.
 */
export type LocalRoster = Readonly<Record<string, string>>;

/** Fallback rendered when a userId is not present in the roster. */
export const UNKNOWN_DISPLAY_NAME = "Unknown user" as const;
